import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { AlimentoCommercialeCounter, Categoria, AlimentiFiltri, ApiResponse } from '../../../types/alimenti';
import Accordion from '../../controls/Accordion';
import PaginationControls from '../../controls/PaginationControls';
import SortIcon from '../../controls/SortIcon';
import useAccordion from '../../../hooks/useAccordion';
import usePagination from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import './AlimentiPersonali.css';


const AlimentiCommerciali: React.FC = () => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState<AlimentoCommercialeCounter[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Hook per ordinamento
  const { sortedData, sortConfig, handleSort } = useSort(risultati, {
    initialField: 'numUtenti',
    initialDirection: 'desc'
  });
  
  // Hook per paginazione
  const {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage, 
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  } = usePagination(sortedData, { initialItemsPerPage: 10 });
  
  // Stato per modal immagini
  const [modalImage, setModalImage] = useState<{src: string, alt: string} | null>(null);
  
  // Filtri
  const [filtri, setFiltri] = useState<AlimentiFiltri>({
    dataDa: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 giorni fa
    dataA: new Date().toISOString().split('T')[0], // oggi
    tipo: 'tutti', // Manteniamo per compatibilità ma non lo mostreremo
    idCategoria: null
  });
  
  // Hook per accordion
  const filtersAccordion = useAccordion(true);
  const statsAccordion = useAccordion(true);

  // Carica categorie al mount
  useEffect(() => {
    caricaCategorie();
  }, []);

  // Gestione chiusura modal con ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalImage) {
        closeImageModal();
      }
    };

    if (modalImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Blocca scroll del body
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modalImage]);

  const caricaCategorie = async () => {
    try {
      const response = await api.get<ApiResponse<Categoria[]>>('/AlimentiCommerciali/categorie');
      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const cercaAlimenti = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA,
        tipo: filtri.tipo,
        idCategoria: filtri.idCategoria || undefined
      };

      const response = await api.post<ApiResponse<AlimentoCommercialeCounter[]>>('/AlimentiCommerciali/conteggio', payload);
      
      if (response.data.success) {
        setRisultati(response.data.data);
        resetToFirstPage(); // Reset alla prima pagina per nuova ricerca
      } else {
        setError(response.data.message || 'Errore nella ricerca');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante la ricerca');
      console.error('Errore ricerca alimenti commerciali:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo: keyof AlimentiFiltri, valore: any) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };

  // Gestione modal immagini
  const openImageModal = (src: string, alt: string) => {
    setModalImage({ src, alt });
  };

  const closeImageModal = () => {
    setModalImage(null);
  };

  // Calcoli statistiche
  const totaleUtilizzi = risultati.reduce((sum, item) => sum + item.numUtenti, 0);
  const mediaUtilizzi = risultati.length > 0 ? (totaleUtilizzi / risultati.length).toFixed(1) : '0';

  return (
    <div className="alimenti-personali-container">
      <div className="page-header">
        <div className="header-top">
          <button 
            className="btn-back" 
            onClick={() => navigate('/')}
            title="Torna alla Dashboard"
          >
            ← Dashboard
          </button>
        </div>
        <div className="header-content-main">
          <div className="header-text">
            <h1>Alimenti Commerciali</h1>
            <p className="page-description">
              Visualizza e analizza gli alimenti commerciali utilizzati dagli utenti nelle diete e nei piatti
            </p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-commerciali')}
              disabled={true}
              title="Sei già nella vista Conteggio"
            >
              📊 Conteggio
            </button>
            
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-commerciali/dettaglio')}
              title="Vai alla vista dettaglio con informazioni nutrizionali e utenti"
            >
              🔍 Dettaglio
            </button>
          </div>
        </div>
      </div>

      {/* Sezione Filtri */}
      <div className="filtri-section">
        <Accordion 
          title="Filtri di Ricerca"
          isOpen={filtersAccordion.isOpen}
          onToggle={filtersAccordion.toggle}
        >
          <div className="filtri-grid">
            <div className="filtro-item">
              <label htmlFor="dataDa">Data Da:</label>
              <input
                type="date"
                id="dataDa"
                value={filtri.dataDa}
                onChange={(e) => handleFiltroChange('dataDa', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="dataA">Data A:</label>
              <input
                type="date"
                id="dataA"
                value={filtri.dataA}
                onChange={(e) => handleFiltroChange('dataA', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="categoria">Categoria:</label>
              <select
                id="categoria"
                value={filtri.idCategoria || ''}
                onChange={(e) => handleFiltroChange('idCategoria', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Tutte le categorie</option>
                {categorie.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.descrizione}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="filtri-actions">
            <button 
              className="btn-primary" 
              onClick={cercaAlimenti}
              disabled={loading}
            >
              {loading ? 'Ricerca in corso...' : 'Cerca'}
            </button>
          </div>
        </Accordion>
      </div>

      {/* Statistiche Rapide */}
      {risultati.length > 0 && (
        <div className="statistiche-section">
          <Accordion 
            title="Statistiche"
            isOpen={statsAccordion.isOpen}
            onToggle={statsAccordion.toggle}
          >
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{risultati.length}</div>
                <div className="stat-label">Alimenti Commerciali</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{totaleUtilizzi}</div>
                <div className="stat-label">Totale Utilizzi</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{mediaUtilizzi}</div>
                <div className="stat-label">Media Utilizzi per Alimento</div>
              </div>
            </div>
          </Accordion>
        </div>
      )}

      {/* Messaggio di errore */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Tabella Risultati */}
      <div className="risultati-section">
        <h2>Risultati</h2>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Caricamento dati...</p>
          </div>
        )}

        {!loading && risultati.length === 0 && !error && (
          <div className="no-results">
            <p>Nessun risultato trovato per i filtri selezionati.</p>
            <p>Prova a modificare i criteri di ricerca.</p>
          </div>
        )}

        {!loading && risultati.length > 0 && (
          <>
            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showBottomControls={false}
            />

            <div className="table-container">
              <table className="risultati-table">
                <thead>
                  <tr>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('descrizione')}
                      title="Clicca per ordinare"
                    >
                      Descrizione <SortIcon field="descrizione" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('produttore')}
                      title="Clicca per ordinare"
                    >
                      Produttore <SortIcon field="produttore" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('categoria')}
                      title="Clicca per ordinare"
                    >
                      Categoria <SortIcon field="categoria" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('pacchetto')}
                      title="Clicca per ordinare"
                    >
                      Pacchetto <SortIcon field="pacchetto" sortConfig={sortConfig} />
                    </th>
                    <th>
                      Codice
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('numUtenti')}
                      title="Clicca per ordinare"
                    >
                      N° Utenti <SortIcon field="numUtenti" sortConfig={sortConfig} />
                    </th>
                    <th>
                      Immagine
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={`${item.idAlimento}-${index}`}>
                      <td className="descrizione">
                        <strong>{item.descrizione}</strong>
                      </td>
                      <td className="produttore">
                        {item.produttore || '-'}
                      </td>
                      <td className="categoria">
                        {item.categoria}
                      </td>
                      <td className="pacchetto">
                        {item.pacchetto}
                      </td>
                      <td className="codice">
                        <code>{item.cod || '-'}</code>
                      </td>
                      <td className="numero-utenti">
                        <span className="badge-numero">{item.numUtenti}</span>
                      </td>
                      <td className="immagine">
                        {item.pic ? (
                          <img 
                            src={item.pic} 
                            alt={item.descrizione}
                            className="thumbnail clickable"
                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px', cursor: 'pointer' }}
                            onClick={() => openImageModal(item.pic!, item.descrizione)}
                            title="Clicca per ingrandire"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <span className="no-image">📷</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showTopControls={false}
            />
          </>
        )}
      </div>

      {/* Modal per le immagini */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeImageModal} title="Chiudi (ESC)">
              ✕
            </button>
            
            <div className="modal-image-container">
              <img 
                src={modalImage.src} 
                alt={modalImage.alt}
                className="modal-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-image.png';
                }}
              />
            </div>
            
            <div className="modal-info">
              <h3>{modalImage.alt}</h3>
              <p>Clicca fuori dall'immagine o premi ESC per chiudere</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlimentiCommerciali;